import React, { Fragment, useEffect } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import * as Icon from "@common/components/DetailsPane/icons";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { useCustomElementInjection } from "@common/hooks/useCustomElementInjection";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import NavigationCustomElement from "@main/layouts/components/PageNavigation/NavigationCustomElement/NavigationCustomElement";
import { ELEMENT_CONTAINER_ID, NAV_LIST } from "@main/layouts/components/PageNavigation/PageNavigation";
import { getActivityHistoryHelperText } from "@main/pages/shared/components/ActivityHistoryTable/ActivityHistoryHelpers";
import { ActivityHistoryTable } from "@main/pages/shared/components/ActivityHistoryTable/ActivityHistoryTable";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./Crashes.styles";
import useCrashEventApiConsumer from "../consumers/useCrashEventApiConsumer";
import useCrashOverviewApiConsumer from "../consumers/useCrashOverviewApiConsumer";

export default function CrashEventActivityHistoryPageView() {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "Crash Activity History" });
  }, [sendEvent]);

  const navigate = useNavigate();
  const params = useParams<{
    workspaceSlug: string;
    appKey: string;
    crashReportKey: string;
    crashReportEventId: string;
  }>();

  const consumer = useCrashOverviewApiConsumer({
    selectedWorkspaceId: selectedWorkspace?.id,
    selectedAppId: selectedApp?.id,
    crashReportKey: params.crashReportKey,
  });

  const eventConsumer = useCrashEventApiConsumer({
    teamId: selectedWorkspace?.id,
    appId: selectedApp?.id,
    crashReportKey: params.crashReportKey,
    crashReportEventId: params.crashReportEventId,
  });

  const CustomElement = consumer.crashOverview ? (
    <Fragment>
      <NavigationCustomElement>
        <Styled.NavigationTitle
          $isButton
          onClick={() =>
            navigate(
              generatePath(RoutePaths.CRASH_REPORT_OVERVIEW, {
                workspaceSlug: selectedWorkspace?.slug ?? null,
                appKey: selectedApp?.key ?? null,
                crashReportKey: params.crashReportKey ?? null,
              }),
            )
          }
        >
          <div>
            <p>{consumer.crashOverview?.title ?? "No description"}</p>
          </div>
        </Styled.NavigationTitle>
        <Styled.NavigationTitle $isButton={false}>
          <Flex>
            <div>
              <Tooltip
                text={"See crash details"}
                position={"bottom"}
              >
                <Styled.NavbarNavigation
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.CRASH_REPORT_EVENT, {
                        workspaceSlug: selectedWorkspace?.slug ?? null,
                        appKey: selectedApp?.key ?? null,
                        crashReportKey: params.crashReportKey ?? null,
                        crashReportEventId: params.crashReportEventId ?? null,
                      }),
                    )
                  }
                >
                  <Icon.SendIcon
                    height={20}
                    width={20}
                  />
                </Styled.NavbarNavigation>
              </Tooltip>
            </div>
            <p>Activity history</p>

            <div>
              <Tooltip
                text={!eventConsumer.crashEvent?.log ? "No logs to download" : "Download as .log file"}
                position="bottom"
              >
                <Styled.DownloadIcon
                  disabled={!eventConsumer.crashEvent?.log}
                  onClick={() => {
                    if (eventConsumer.crashEvent?.log) eventConsumer.downloadActivityHistory();
                  }}
                  $disabled={!eventConsumer.crashEvent?.log}
                />
              </Tooltip>
            </div>
          </Flex>
        </Styled.NavigationTitle>
        {getActivityHistoryHelperText()}
      </NavigationCustomElement>
    </Fragment>
  ) : (
    <Fragment />
  );

  useCustomElementInjection({
    parentElementId: NAV_LIST,
    element: CustomElement,
    containerId: ELEMENT_CONTAINER_ID,
    tagName: "li",
  });

  if (consumer.error) {
    return (
      <ErrorStateView
        title="There was an error loading ticket. Try refreshing."
        subtitle="The link you followed may be broken, or the ticket may have been removed."
      />
    );
  }

  if (!params.crashReportEventId) return <Fragment />;

  if (!eventConsumer.crashEvent) {
    return <Spinner />;
  }

  return (
    <ActivityHistoryTable
      ticketKey={params.crashReportEventId}
      hasLogs={Boolean(eventConsumer.crashEvent?.log)}
      type="crash"
    />
  );
}
